import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { deleteAccount, formatApiError } from "../lib/api";

const Steps = () => (
  <div data-testid="delete-account-steps" className="what-plan mt-8 text-sm leading-relaxed">
    <p className="font-display font-semibold text-white">How to delete your account</p>
    <ol className="list-decimal pl-5 mt-3 space-y-1.5">
      <li>Log in to WHAT? on this website or in the phone app.</li>
      <li>Open the menu and choose <span className="text-white">Delete account</span>, or come back to this page.</li>
      <li>Confirm with your password (and the grown-up PIN if Kids Mode is on).</li>
    </ol>
    <p className="mt-3 text-slate-400">Can't log in? Email <a href="mailto:privacy@askwhat.co.za" className="text-[#D4FF3A]">privacy@askwhat.co.za</a> from your account's email address and we'll delete it for you.</p>
  </div>
);

const DeleteForm = ({ user }) => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await deleteAccount({ confirm, password: password || undefined, pin: pin || undefined });
      if (r.store_note) toast.message(r.store_note, { duration: 12000 });
      toast.success("Your account and its data have been deleted.");
      setUser(false);
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };
  if (user.role === "student") {
    return <p data-testid="delete-account-student" className="what-plan mt-8 text-sm">Your login was made by your teacher or parent. Ask them to remove your account.</p>;
  }
  return (
    <form onSubmit={submit} data-testid="delete-account-form" className="what-plan mt-8 space-y-4">
      <p className="text-sm">Signed in as <span className="text-white">{user.email}</span></p>
      {user.has_password !== false && (
        <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">Password
          <input data-testid="delete-account-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="what-input mt-2 normal-case tracking-normal" autoComplete="current-password" />
        </label>
      )}
      {user.kids_mode && (
        <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">Grown-up PIN (Kids Mode is on)
          <input data-testid="delete-account-pin" type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} className="what-input mt-2" />
        </label>
      )}
      <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">Type DELETE to confirm
        <input data-testid="delete-account-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="what-input mt-2" />
      </label>
      <button data-testid="delete-account-submit" disabled={busy || confirm.trim().toUpperCase() !== "DELETE"} className="w-full py-3 rounded-xl font-semibold bg-red-500 text-white hover:bg-red-400 disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
        {busy && <Loader2 size={15} className="animate-spin" />} Delete my account forever
      </button>
    </form>
  );
};

export default function DeleteAccountPage() {
  const { user } = useAuth();
  return (
    <div data-testid="delete-account-page" className="min-h-screen bg-[#0B0F17] text-slate-300">
      <div className="max-w-xl mx-auto px-6 py-14">
        <Link to="/" className="text-xs font-mono text-slate-500 hover:text-white flex items-center gap-1.5"><ArrowLeft size={13} /> WHAT?</Link>
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-white mt-8">Delete your account</h1>
        <div className="mt-6 flex gap-3 rounded-xl border border-red-400/30 bg-red-400/5 p-4 text-sm text-red-100">
          <AlertTriangle size={18} className="shrink-0 text-red-300" />
          <div>
            <p>This permanently deletes your WHAT? account and its data: chats, debates, saved words, quiz scores and badges. If you manage a class or homeschool family, those classes and the children's logins are deleted too.</p>
            <p className="mt-2 text-red-200/80">Website subscriptions (PayFast/Paystack) are cancelled automatically. Subscriptions bought in the phone app must also be cancelled in Google Play or the App Store. We keep basic payment records without your name or email, as tax law requires. See our <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
          </div>
        </div>
        {user ? <DeleteForm user={user} /> : (
          <>
            <Steps />
            {user === false && <Link to="/login" state={{ from: "/delete-account" }} data-testid="delete-account-login-link" className="what-btn-primary inline-block mt-6">Log in to delete</Link>}
          </>
        )}
      </div>
    </div>
  );
}
